export const PENDING_STREAM_STORAGE_PREFIX = 'mark-pending-stream:';
export const PENDING_STREAM_POLL_INTERVAL_MS = 3000;
export const PENDING_STREAM_MAX_AGE_MS = 5 * 60 * 1000;
export const PENDING_STREAM_LOADING_CONTENT = 'Generating response...';

export type PendingStream =
  | {
      type: 'send';
      conversationId: string;
      startedAt: number;
    }
  | {
      type: 'regenerate' | 'edit';
      conversationId: string;
      messageId: string;
      messageIndex: number;
      contentSnapshot: string;
      startedAt: number;
    };

interface ChatMessageLike {
  id?: string;
  content?: string;
  isUser?: boolean;
  loading?: boolean;
}

const storageKey = (conversationId: string): string =>
  `${PENDING_STREAM_STORAGE_PREFIX}${conversationId}`;

export const setPendingStream = (pending: PendingStream): void => {
  try {
    sessionStorage.setItem(
      storageKey(pending.conversationId),
      JSON.stringify(pending)
    );
  } catch {
    // Ignore quota / private-mode failures; recovery is best-effort.
  }
};

export const clearPendingStream = (conversationId: string): void => {
  try {
    sessionStorage.removeItem(storageKey(conversationId));
  } catch {
    // Ignore storage failures.
  }
};

export const getPendingStream = (
  conversationId: string | null | undefined
): PendingStream | null => {
  if (!conversationId) return null;

  try {
    const raw = sessionStorage.getItem(storageKey(conversationId));

    if (!raw) return null;

    const pending = JSON.parse(raw) as PendingStream;

    if (
      !pending ||
      typeof pending !== 'object' ||
      typeof pending.startedAt !== 'number' ||
      pending.conversationId !== conversationId
    ) {
      clearPendingStream(conversationId);

      return null;
    }

    if (Date.now() - pending.startedAt > PENDING_STREAM_MAX_AGE_MS) {
      clearPendingStream(conversationId);

      return null;
    }

    if (
      (pending.type === 'regenerate' || pending.type === 'edit') &&
      (typeof pending.messageId !== 'string' ||
        typeof pending.messageIndex !== 'number')
    ) {
      clearPendingStream(conversationId);

      return null;
    }

    return pending;
  } catch {
    clearPendingStream(conversationId);

    return null;
  }
};

export const isAwaitingAssistantReply = (
  messages: ChatMessageLike[] | null | undefined
): boolean => {
  if (!messages?.length) return false;

  return Boolean(messages[messages.length - 1]?.isUser);
};

/**
 * Looks up the pending target message by its position rather than its id — the
 * backend may replace the row (new id) once regeneration/edit lands, so an
 * id-based lookup can go missing mid-stream and be misread as "already done".
 * A missing/not-yet-updated target means generation is still in flight.
 */
export const isPendingTargetStillPending = (
  messages: ChatMessageLike[] | null | undefined,
  pending: Extract<PendingStream, { type: 'regenerate' | 'edit' }>
): boolean => {
  const target = messages?.[pending.messageIndex];

  if (!target || target.isUser) return true;

  return (target.content ?? '') === pending.contentSnapshot;
};

/**
 * Regenerating or editing a message discards conversation history after it (see
 * handleRegenerateMessage/handleEditMessage's client-side slice) even though the
 * backend's message history may still return those trailing rows — so recovery
 * must re-apply the same truncation to the polled server messages.
 */
export const truncateToPendingTarget = <T extends ChatMessageLike>(
  messages: T[],
  pending: PendingStream | null
): T[] => {
  if (!pending || pending.type === 'send') return messages;

  return messages.slice(0, pending.messageIndex + 1);
};

export const shouldPollForPendingStream = (
  messages: ChatMessageLike[] | null | undefined,
  pending: PendingStream | null
): boolean => {
  if (!pending) return false;

  // Session history can lag right after disconnect — keep polling while empty.
  if (!messages?.length) return true;

  if (pending.type === 'send') {
    return isAwaitingAssistantReply(messages);
  }

  return isPendingTargetStillPending(messages, pending);
};

/** Browser refresh/unload often rejects fetch as TypeError / "Failed to fetch". */
export const isLikelyPageUnloadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  if (error.name === 'AbortError') return true;

  return (
    error instanceof TypeError ||
    /failed to fetch|networkerror|load failed/i.test(error.message)
  );
};

export const withPendingLoadingState = <T extends ChatMessageLike>(
  messages: T[],
  pending: PendingStream | null
): T[] => {
  if (!pending) return messages;

  if (pending.type === 'send') {
    if (messages.length > 0 && !isAwaitingAssistantReply(messages)) {
      return messages;
    }

    if (messages.some((message) => message.loading && !message.isUser)) {
      return messages;
    }

    return [
      ...messages,
      {
        id: `pending-stream-${pending.conversationId}`,
        content: PENDING_STREAM_LOADING_CONTENT,
        isUser: false,
        loading: true,
      } as T,
    ];
  }

  if (!messages.length || !isPendingTargetStillPending(messages, pending)) {
    return messages;
  }

  const truncated = truncateToPendingTarget(messages, pending);

  // The backend may not yet have a row at the regenerated position (e.g. it
  // clears/recreates the message before streaming the new content) — in that
  // case there is nothing at pending.messageIndex to map over, so append a
  // placeholder instead of relying on .map(), which can't extend the array.
  if (truncated.length <= pending.messageIndex) {
    return [
      ...truncated,
      {
        id: pending.messageId,
        content: PENDING_STREAM_LOADING_CONTENT,
        isUser: false,
        loading: true,
      } as T,
    ];
  }

  return truncated.map((message, index) =>
    index === pending.messageIndex
      ? {
          ...message,
          content: PENDING_STREAM_LOADING_CONTENT,
          loading: true,
        }
      : message
  );
};
